import { notificationDesign } from '../../utils/notificationDesign';
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { App, Button } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../../features/access/context/AccessProvider';
import { useSocket } from '../contexts/SocketContext';
import { draftKey, emptyDraft, loadLocalDraft, mailError, mailRequest, saveLocalDraft } from './mailboxModel';
import MailComposer from './MailComposer';
import './mailbox.css';

const Context = createContext(null);
export const useMailbox = () => useContext(Context);
export function MailboxProvider({ children }) {
  const { authState } = useAuth();
  const { can, status } = useAccess();
  const enabled = !!authState?.isAuthenticated && status === 'ready' && can('mailbox.view');
  const identity = authState?.isAuthenticated ? String(authState.id) : '';
  // Remount all mailbox state on identity/access changes, including pending requests.
  return <MailboxSession key={identity} enabled={enabled} userId={identity}>{children}</MailboxSession>;
}
function MailboxSession({ enabled, userId, children }) {
  const { can } = useAccess();
  const { socket } = useSocket();
  const { notification, message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [draft, setDraft] = useState(null);
  const [storageError, setStorageError] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [revision, setRevision] = useState(0);
  const [labels, setLabels] = useState([]);
  const [labelsError, setLabelsError] = useState('');
  const [labelsRevision, setLabelsRevision] = useState(0);
  const refreshLabels = useCallback(() => setLabelsRevision(value => value + 1), []);
  useEffect(() => {
    if (!enabled || !profile) return undefined;
    let active = true;
    mailRequest('/labels').then(data => {
      if (active) { setLabels(data); setLabelsError(''); }
    }).catch(failure => {
      if (active) { setLabels(current => current.map(label => ({ ...label, messages_unread: null, messages_total: null }))); setLabelsError(mailError(failure)); }
    });
    return () => { active = false; };
  }, [enabled, profile, revision, labelsRevision]);
  useEffect(() => {
    if (!enabled || !profile) return undefined;
    socket?.on('connect', refreshLabels);
    window.addEventListener('focus', refreshLabels);
    return () => { socket?.off('connect', refreshLabels); window.removeEventListener('focus', refreshLabels); };
  }, [enabled, profile, socket, refreshLabels]);
  const live = useRef(true);
  const seen = useRef(new Set());
  const acknowledged = useRef(new Set());
  const notificationAccess = useRef(enabled);
  notificationAccess.current = enabled;
  useEffect(() => () => {
    live.current = false;
    seen.current.forEach(id => notification.destroy(`mail-${id}`));
  }, [notification]);
  useEffect(() => {
    if (!enabled) seen.current.forEach(id => notification.destroy(`mail-${id}`));
  }, [enabled, notification]);
  const key = profile ? draftKey(userId, profile.primary_email) : null;
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  const initialize = useCallback(async () => {
    if (!enabled) return;
    setLoading(true); setError(null);
    try {
      const data = await mailRequest('/me');
      if (!live.current) return;
      setProfile(data);
      try { setDraft(loadLocalDraft(draftKey(userId, data.primary_email))); } catch (failure) { setStorageError(mailError(failure)); }
    } catch (failure) { if (live.current) setError(failure); }
    finally { if (live.current) setLoading(false); }
  }, [enabled, userId]);
  useEffect(() => { if (enabled && !profile) initialize(); }, [initialize, enabled, profile]);
  useEffect(() => {
    if (!key || !draft) return;
    try { saveLocalDraft(key, draft); setStorageError(''); } catch (_) { setStorageError('This draft could not be saved on this browser. Keep this window open and free browser storage before sending.'); }
  }, [key, draft]);
  useEffect(() => {
    if (!key) return undefined;
    const changed = event => {
      if (event.key !== key) return;
      try { setDraft(loadLocalDraft(key)); } catch (_) { setStorageError('The draft changed in another tab and could not be loaded.'); }
    };
    const warn = event => { if (storageError) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    window.addEventListener('storage', changed);
    return () => { window.removeEventListener('beforeunload', warn); window.removeEventListener('storage', changed); };
  }, [key, storageError]);
  const acknowledge = useCallback(async id => {
    if (!live.current || !notificationAccess.current || acknowledged.current.has(id)) return;
    acknowledged.current.add(id);
    try { await mailRequest(`/notifications/${id}/acknowledge`, { method: 'post' }); if (live.current) setNotifications(rows => rows.filter(row => row.id !== id)); }
    catch (failure) { acknowledged.current.delete(id); if (live.current) message.error(mailError(failure)); }
  }, [message]);
  useEffect(() => {
    if (!enabled || !profile) return undefined;
    let active = true;
    const load = () => mailRequest('/notifications', { params: { unacknowledged_only: true, limit: 50 } }).then(rows => { if (active) setNotifications(rows); }).catch(() => {});
    load();
    const arrival = payload => {
      const row = payload?.data;
      if (!row?.id || seen.current.has(row.id)) return;
      seen.current.add(row.id); refresh(); load();
      if (location.pathname === `/mail/${row.gmail_thread_id}`) return;
      notification.info({
      ...notificationDesign('mail', 'info'), key: `mail-${row.id}`, message: row.from || 'New email', description: row.subject || '(No subject)', duration: 0, placement: 'topRight',
        onClose: () => acknowledge(row.id),
        btn: <><Button size="small" onClick={() => { navigate(`/mail/${row.gmail_thread_id}`); acknowledge(row.id); notification.destroy(`mail-${row.id}`); }}>Open email</Button><Button size="small" type="text" onClick={() => { acknowledge(row.id); notification.destroy(`mail-${row.id}`); }}>Dismiss</Button></> });
    };
    socket?.on('mailbox:new_message', arrival);
    const updated = payload => {
      const row = payload?.data;
      if (row?.acknowledged_at) { acknowledged.current.add(row.id); notification.destroy(`mail-${row.id}`); }
      load();
    };
    socket?.on('mailbox:notification_updated', updated);
    socket?.on('connect', load);
    return () => { active = false; socket?.off('mailbox:new_message', arrival); socket?.off('mailbox:notification_updated', updated); socket?.off('connect', load); };
  }, [enabled, profile, socket, refresh, location.pathname, navigate, notification, acknowledge]);
  const compose = seed => {
    if (!profile || !can('mailbox.send')) return;
    if (draft) { setDraft(current => ({ ...current, closed: false, minimized: false })); message.info('Your current draft is open. Send or discard it before starting another.'); return; }
    setDraft({ ...emptyDraft(), ...seed, delivery_key: crypto.randomUUID() });
  };
  const clearDraft = () => {
    try { localStorage.removeItem(key); setDraft(null); setStorageError(''); return true; }
    catch (_) { setStorageError('Unable to clear the saved draft. Please free browser storage.'); return false; }
  };
  const persistCommand = next => { saveLocalDraft(key, next); setDraft(next); };
  const inboxUnread = enabled ? labels.find(label => label.id === 'INBOX')?.messages_unread ?? null : null;
  const value = { labels, labelsError, refreshLabels, inboxUnread, profile, error, loading, initialize, draft, setDraft, compose, clearDraft, persistCommand, storageError, notifications, acknowledge, revision, refresh };
  return <Context.Provider value={value}>{children}{enabled && profile && can('mailbox.send') && draft && !draft.closed && <MailComposer />}</Context.Provider>;
}
